import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * DateRangeSlider - A touch-friendly dual-handle slider for selecting date ranges
 *
 * Props:
 * - data: Array of chart data points with 'period' property
 * - onRangeSelect: Callback function (startPeriod, endPeriod) => void
 * - theme: Theme object for styling
 * - orientation: 'horizontal' | 'vertical' (default: 'horizontal')
 */
function DateRangeSlider({ data, onRangeSelect, theme, orientation = 'horizontal' }) {
  const { t } = useLanguage();

  // State for handle positions (indices into data array)
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(data?.length ? data.length - 1 : 0);

  // Track which handle is being dragged
  const [draggingHandle, setDraggingHandle] = useState(null); // 'start' | 'end' | null

  // Refs
  const trackRef = useRef(null);
  const startHandleRef = useRef(null);
  const endHandleRef = useRef(null);

  // Store draggingHandle in ref to avoid stale closures in event listeners
  const draggingHandleRef = useRef(null);

  // Store current indices in refs to avoid stale closures
  const startIndexRef = useRef(startIndex);
  const endIndexRef = useRef(endIndex);

  // Update refs when state changes
  useEffect(() => {
    startIndexRef.current = startIndex;
    endIndexRef.current = endIndex;
  }, [startIndex, endIndex]);

  // Trigger haptic feedback if available
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(10); // 10ms vibration
    }
  }, []);

  // Convert position (0-1) to data index, snapping to nearest data point
  const positionToIndex = useCallback((position) => {
    const clampedPosition = Math.max(0, Math.min(1, position));
    const rawIndex = clampedPosition * (data.length - 1);
    return Math.round(rawIndex);
  }, [data]);

  // Convert data index to position (0-1)
  const indexToPosition = useCallback((index) => {
    if (data.length <= 1) return 0;
    return index / (data.length - 1);
  }, [data]);

  // Get position from touch/mouse event
  const getPositionFromEvent = useCallback((e, trackElement) => {
    const rect = trackElement.getBoundingClientRect();

    // Get client coordinates (works for both touch and mouse)
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    if (orientation === 'horizontal') {
      const x = clientX - rect.left;
      return x / rect.width;
    } else {
      const y = clientY - rect.top;
      return y / rect.height;
    }
  }, [orientation]);

  // Handle move - Touch/Mouse Move (defined first to avoid circular dependency)
  const handleMove = useCallback((e) => {
    if (!draggingHandleRef.current || !trackRef.current) return;

    e.preventDefault();

    const position = getPositionFromEvent(e, trackRef.current);
    const newIndex = positionToIndex(position);

    if (draggingHandleRef.current === 'start') {
      // Start handle cannot go past end handle
      const clampedIndex = Math.min(newIndex, endIndexRef.current);
      setStartIndex(clampedIndex);
    } else if (draggingHandleRef.current === 'end') {
      // End handle cannot go before start handle
      const clampedIndex = Math.max(newIndex, startIndexRef.current);
      setEndIndex(clampedIndex);
    }
  }, [getPositionFromEvent, positionToIndex]);

  // Handle end - Touch/Mouse Up
  const handleEnd = useCallback((e) => {
    if (!draggingHandleRef.current) return;

    e.preventDefault();

    // Trigger haptic on release
    triggerHaptic();

    // Call callback with selected range (use refs to get current values)
    const currentStartIndex = startIndexRef.current;
    const currentEndIndex = endIndexRef.current;

    if (currentStartIndex !== currentEndIndex && onRangeSelect && data) {
      const startPeriod = data[currentStartIndex].period;
      const endPeriod = data[currentEndIndex].period;

      console.log('DateRangeSlider - Calling onRangeSelect:', {
        currentStartIndex,
        currentEndIndex,
        startPeriod,
        endPeriod,
        dataLength: data.length
      });

      onRangeSelect(startPeriod, endPeriod);
    } else {
      console.log('DateRangeSlider - NOT calling onRangeSelect:', {
        currentStartIndex,
        currentEndIndex,
        sameIndex: currentStartIndex === currentEndIndex,
        hasCallback: !!onRangeSelect,
        hasData: !!data
      });
    }

    // Clean up event listeners
    document.removeEventListener('touchmove', handleMove);
    document.removeEventListener('touchend', handleEnd);
    document.removeEventListener('mousemove', handleMove);
    document.removeEventListener('mouseup', handleEnd);

    draggingHandleRef.current = null;
    setDraggingHandle(null);
  }, [data, onRangeSelect, triggerHaptic, handleMove]);

  // Handle start - Touch/Mouse Down
  const handleStart = useCallback((handle, e) => {
    e.preventDefault();
    e.stopPropagation();

    draggingHandleRef.current = handle;
    setDraggingHandle(handle);
    triggerHaptic();

    // For touch events
    if (e.touches) {
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd, { passive: false });
    }
    // For mouse events
    else {
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
    }
  }, [triggerHaptic, handleMove, handleEnd]);

  // Format period label (keep it concise for display)
  const formatPeriod = useCallback((period) => {
    if (!period) return '';

    // Try to parse as ISO date
    if (period.match(/^\d{4}-\d{2}-\d{2}/)) {
      const date = new Date(period);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    }

    // Week format (e.g., "2024-W01")
    if (period.match(/^\d{4}-W\d{2}/)) {
      return period.substring(5); // Return "W01"
    }

    // Month format (e.g., "2024-01")
    if (period.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = period.split('-');
      const date = new Date(year, month - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }

    // Default: return as-is
    return period;
  }, []);

  // Always reset slider to full range when data changes
  useEffect(() => {
    if (data && data.length > 0 && !draggingHandleRef.current) {
      // Always reset to full range when new data arrives
      setStartIndex(0);
      setEndIndex(data.length - 1);
    }
  }, [data]);

  // Attach native event listeners to handles (to support { passive: false })
  useEffect(() => {
    if (!startHandleRef.current || !endHandleRef.current) return;

    const startHandle = startHandleRef.current;
    const endHandle = endHandleRef.current;

    const createStartHandler = (handle) => (e) => {
      handleStart(handle, e);
    };

    const startHandleListener = createStartHandler('start');
    const endHandleListener = createStartHandler('end');

    // Touch events with passive: false to allow preventDefault
    startHandle.addEventListener('touchstart', startHandleListener, { passive: false });
    endHandle.addEventListener('touchstart', endHandleListener, { passive: false });

    // Mouse events (passive is not an issue)
    startHandle.addEventListener('mousedown', startHandleListener);
    endHandle.addEventListener('mousedown', endHandleListener);

    return () => {
      startHandle.removeEventListener('touchstart', startHandleListener);
      endHandle.removeEventListener('touchstart', endHandleListener);
      startHandle.removeEventListener('mousedown', startHandleListener);
      endHandle.removeEventListener('mousedown', endHandleListener);
    };
  }, [handleStart]);

  // Early return if no data
  if (!data || data.length === 0) {
    return null;
  }

  // Calculate positions for handles
  const startPos = indexToPosition(startIndex);
  const endPos = indexToPosition(endIndex);

  // Styling
  const trackStyle = orientation === 'horizontal'
    ? { width: '100%', height: '8px' }
    : { width: '8px', height: '100%' };

  const rangeStyle = orientation === 'horizontal'
    ? {
        left: `${startPos * 100}%`,
        width: `${(endPos - startPos) * 100}%`,
        height: '100%'
      }
    : {
        top: `${startPos * 100}%`,
        height: `${(endPos - startPos) * 100}%`,
        width: '100%'
      };

  const handleBaseStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    position: 'absolute',
    cursor: 'grab',
    transform: orientation === 'horizontal'
      ? 'translate(-50%, -50%)'
      : 'translate(-50%, -50%)',
    transition: draggingHandle ? 'none' : 'all 0.2s ease',
    boxShadow: `0 2px 4px rgba(0,0,0,0.2)`,
    touchAction: 'none' // Prevent scrolling while dragging
  };

  const startHandleStyle = orientation === 'horizontal'
    ? {
        ...handleBaseStyle,
        left: `${startPos * 100}%`,
        top: '50%',
        backgroundColor: draggingHandle === 'start'
          ? theme?.colors?.primary || '#1976d2'
          : theme?.colors?.surface || '#fff',
        border: `3px solid ${theme?.colors?.primary || '#1976d2'}`,
        transform: draggingHandle === 'start'
          ? 'translate(-50%, -50%) scale(1.1)'
          : 'translate(-50%, -50%)'
      }
    : {
        ...handleBaseStyle,
        top: `${startPos * 100}%`,
        left: '50%',
        backgroundColor: draggingHandle === 'start'
          ? theme?.colors?.primary || '#1976d2'
          : theme?.colors?.surface || '#fff',
        border: `3px solid ${theme?.colors?.primary || '#1976d2'}`,
        transform: draggingHandle === 'start'
          ? 'translate(-50%, -50%) scale(1.1)'
          : 'translate(-50%, -50%)'
      };

  const endHandleStyle = orientation === 'horizontal'
    ? {
        ...handleBaseStyle,
        left: `${endPos * 100}%`,
        top: '50%',
        backgroundColor: draggingHandle === 'end'
          ? theme?.colors?.primary || '#1976d2'
          : theme?.colors?.surface || '#fff',
        border: `3px solid ${theme?.colors?.primary || '#1976d2'}`,
        transform: draggingHandle === 'end'
          ? 'translate(-50%, -50%) scale(1.1)'
          : 'translate(-50%, -50%)'
      }
    : {
        ...handleBaseStyle,
        top: `${endPos * 100}%`,
        left: '50%',
        backgroundColor: draggingHandle === 'end'
          ? theme?.colors?.primary || '#1976d2'
          : theme?.colors?.surface || '#fff',
        border: `3px solid ${theme?.colors?.primary || '#1976d2'}`,
        transform: draggingHandle === 'end'
          ? 'translate(-50%, -50%) scale(1.1)'
          : 'translate(-50%, -50%)'
      };

  return (
    <div style={{
      padding: orientation === 'horizontal' ? '20px 0' : '0 20px',
      userSelect: 'none'
    }}>
      {/* Instructions label */}
      <div style={{
        fontSize: '14px',
        color: theme?.colors?.textSecondary || '#666',
        marginBottom: '10px',
        textAlign: 'center'
      }}>
        {t('selectTimeRange')}
      </div>

      {/* Slider container */}
      <div style={{
        display: 'flex',
        flexDirection: orientation === 'horizontal' ? 'column' : 'row',
        alignItems: 'center',
        gap: '10px'
      }}>
        {/* Date labels */}
        <div style={{
          display: 'flex',
          flexDirection: orientation === 'horizontal' ? 'row' : 'column',
          justifyContent: 'space-between',
          width: orientation === 'horizontal' ? '100%' : 'auto',
          fontSize: '12px',
          color: theme?.colors?.text || '#000',
          fontWeight: '500'
        }}>
          <span>{formatPeriod(data[startIndex].period)}</span>
          <span>{formatPeriod(data[endIndex].period)}</span>
        </div>

        {/* Track container */}
        <div style={{
          position: 'relative',
          ...trackStyle,
          backgroundColor: theme?.colors?.border || '#ddd',
          borderRadius: '4px',
          minWidth: orientation === 'vertical' ? '8px' : '200px',
          minHeight: orientation === 'horizontal' ? '8px' : '200px'
        }}>
          {/* Track (full range) */}
          <div
            ref={trackRef}
            style={{
              position: 'absolute',
              ...trackStyle,
              top: 0,
              left: 0
            }}
          />

          {/* Selected range */}
          <div style={{
            position: 'absolute',
            ...rangeStyle,
            backgroundColor: theme?.colors?.primary || '#1976d2',
            opacity: 0.3,
            borderRadius: '4px',
            pointerEvents: 'none'
          }} />

          {/* Start handle */}
          <div
            ref={startHandleRef}
            style={startHandleStyle}
            aria-label={`${t('startDate')}: ${formatPeriod(data[startIndex].period)}`}
            role="slider"
            aria-valuemin="0"
            aria-valuemax={data.length - 1}
            aria-valuenow={startIndex}
            tabIndex="0"
          />

          {/* End handle */}
          <div
            ref={endHandleRef}
            style={endHandleStyle}
            aria-label={`${t('endDate')}: ${formatPeriod(data[endIndex].period)}`}
            role="slider"
            aria-valuemin="0"
            aria-valuemax={data.length - 1}
            aria-valuenow={endIndex}
            tabIndex="0"
          />
        </div>
      </div>

      {/* Helper text */}
      <div style={{
        fontSize: '12px',
        color: theme?.colors?.textSecondary || '#666',
        marginTop: '8px',
        textAlign: 'center'
      }}>
        {t('dragToAdjust')}
      </div>
    </div>
  );
}

export default DateRangeSlider;
